import { computed, ref } from 'vue'
import * as ActivityApi from '@/api/promotion/activity'
import * as PrizeApi from '@/api/promotion/activityPrize'
import * as TemplateApi from '@/api/promotion/activityTemplate'
import {
  getMarketPortalEditorInit,
  type MarketPortalEditorInitVO,
  type MarketPortalEditorPrizeVO
} from '@/api/market/portal'
import type { MarketPrizeRow } from '../mock/types'
import { isLotteryPlayStyle, LOTTERY_PLAY_STYLES, playStyleLabel } from './playStyles'

const LOTTERY_STYLES = LOTTERY_PLAY_STYLES

function mobileH5Base(): string {
  return String(
    import.meta.env.VITE_MOBILE_H5_DOMAIN ||
      import.meta.env.VITE_MOBILE_BASE_URL ||
      location.origin
  ).replace(/\/$/, '')
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** 与 Element Plus datetimerange 的 value-format 一致 */
function formatDateTimeValue(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
}

function toBackendDateTime(local: unknown): string {
  const normalized = normalizeDateTime(local)
  if (!normalized) return ''
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(normalized)) {
    return `${normalized}:00`
  }
  return normalized
}

function defaultEndTime(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  d.setHours(23, 59, 59, 0)
  return formatDateTimeValue(d)
}

function defaultStartTime(): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return formatDateTimeValue(d)
}

function mapPrizeRows(rows?: MarketPortalEditorPrizeVO[]): MarketPrizeRow[] {
  if (!rows?.length) return []
  return rows.map((p) => ({
    name: p.name || '奖品',
    type: p.type || '实物',
    count: p.count ?? 1,
    probability: p.probability ?? 0,
    prizeType: p.prizeType ?? 4
  }))
}

function prizeTypeFromLabel(type: string, explicit?: number): number {
  if (explicit) return explicit
  if (type.includes('券')) return 1
  if (type.includes('积分')) return 2
  if (type.includes('红包') || type.includes('现金')) return 3
  if (type.includes('空') || type.includes('谢谢')) return 6
  if (type.includes('虚拟') || type.includes('卡券')) return 5
  return 4
}

function buildConfigPrizeSnapshot(rows: (MarketPrizeRow & { prizeType?: number; id?: number })[]) {
  return rows.map((row, index) => ({
    id: row.id,
    name: row.name,
    type: row.type,
    prizeType: prizeTypeFromLabel(String(row.type), row.prizeType),
    typeLabel: row.type,
    count: Number(row.count) || 0,
    sort: index
  }))
}

/** 兼容后端 LocalDateTime：字符串 / 毫秒时间戳 / [y,m,d,h,mi,s] 数组 */
function normalizeDateTime(raw?: unknown): string {
  if (raw == null || raw === '') return ''
  if (typeof raw === 'number') {
    const d = new Date(raw)
    return Number.isNaN(d.getTime()) ? '' : formatDateTimeValue(d)
  }
  if (Array.isArray(raw)) {
    const [y, m, d, h = 0, min = 0, s = 0] = raw
    if (typeof y === 'number') {
      return `${y}-${pad2(m)}-${pad2(d)} ${pad2(h)}:${pad2(min)}:${pad2(s)}`
    }
    return ''
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, number>
    if (typeof o.year === 'number') {
      const month = o.monthValue ?? o.month ?? 1
      const day = o.dayOfMonth ?? o.day ?? 1
      return `${o.year}-${pad2(month)}-${pad2(day)} ${pad2(o.hour ?? 0)}:${pad2(o.minute ?? 0)}:${pad2(o.second ?? 0)}`
    }
    return ''
  }
  const text = String(raw)
  return text.replace('T', ' ').slice(0, 19)
}

function prizeTypeToLabel(prizeType?: number): string {
  if (prizeType === 1) return '优惠券'
  if (prizeType === 2) return '积分'
  if (prizeType === 3) return '现金'
  if (prizeType === 5) return '虚拟'
  if (prizeType === 6) return '空'
  return '实物'
}

function mapActivityPrizeRows(list: PrizeApi.ActivityPrizeVO[]): (MarketPrizeRow & { prizeType?: number; id?: number })[] {
  return (list || [])
    .slice()
    .sort((a, b) => (a.sorted ?? 0) - (b.sorted ?? 0))
    .map((p) => ({
      id: p.id,
      name: p.name || '奖品',
      type: prizeTypeToLabel(p.prizeType),
      count: p.totalStock ?? 0,
      probability: p.probability ?? Math.round((p.winRate ?? 0) / 100),
      prizeType: p.prizeType
    }))
}

export type MarketEditorMode = 'create' | 'edit' | 'detail' | 'preview'

const PAGE_TABS = [
  { key: 'rules', title: '活动规则' },
  { key: 'myAwards', title: '我的奖品' },
  { key: 'winners', title: '中奖名单' }
]

type PageTabItem = { key: string; title: string }

function normalizePageTabs(raw?: unknown): PageTabItem[] {
  if (!Array.isArray(raw) || !raw.length) return [...PAGE_TABS]
  const mapped = raw
    .map((item, index) => {
      if (typeof item === 'string') return { key: `tab-${index}`, title: item }
      const row = item as Record<string, unknown>
      const title = String(row.title || row.name || '').trim()
      if (!title) return null
      return { key: String(row.key || `tab-${index}`), title }
    })
    .filter(Boolean) as PageTabItem[]
  return mapped.length ? mapped : [...PAGE_TABS]
}

export function useMarketActivityEditor(
  toast: (msg: string) => void,
  getCoverUrl: (id: number) => string | undefined
) {
  const loading = ref(false)
  const saving = ref(false)
  const initData = ref<MarketPortalEditorInitVO>()
  const activityId = ref<number>()
  const promotionTemplateId = ref<number>()
  const playStyle = ref('wheel')
  const styleName = ref('大转盘抽奖')
  const published = ref(false)
  const editorMode = ref<MarketEditorMode>('create')
  const activityStatus = ref<number>()

  const actName = ref('')
  const startTime = ref(defaultStartTime())
  const endTime = ref(defaultEndTime(7))
  const actRules = ref('')
  const dailyLimit = ref(3)
  const shareExtra = ref(true)
  const shareTitle = ref('')
  const shareDesc = ref('')
  const needFollow = ref(false)
  const phoneAuth = ref(true)
  const antiCheat = ref(true)
  const themeColor = ref('#ff6c6c')
  const themeColors = ref<string[]>([])
  const selectedBgId = ref<number>()
  const prizes = ref<(MarketPrizeRow & { prizeType?: number; id?: number })[]>([])
  const pageTabs = ref<PageTabItem[]>([...PAGE_TABS])

  const activityLink = computed(() => {
    if (!activityId.value) return ''
    const path = initData.value?.playPagePath || '/pages/modules/promotion/pages/play/index'
    return `${mobileH5Base()}${path}?activityId=${activityId.value}&style=${playStyle.value}`
  })

  const editorTitle = computed(() => {
    const label = styleName.value || '营销活动'
    if (editorMode.value === 'detail') return `活动详情 · ${label}`
    if (editorMode.value === 'preview') return `活动预览 · ${label}`
    if (editorMode.value === 'edit') return `编辑活动 · ${label}`
    return `创建活动 · ${label}`
  })

  const readonly = computed(() => editorMode.value === 'detail' || editorMode.value === 'preview')

  const resolvePromotionTemplate = async (style: string) => {
    let list = await TemplateApi.getTemplateMetaList()
    let meta = list?.find((m) => m.style === style && m.registered && m.templateId)
    if (!meta) {
      try {
        await TemplateApi.syncFromPlugins()
        list = await TemplateApi.getTemplateMetaList()
        meta = list?.find((m) => m.style === style && m.registered && m.templateId)
      } catch {
        /* ignore */
      }
    }
    if (!meta?.templateId) {
      throw new Error(`玩法「${style}」尚未登记到活动模板库，请先在「活动模板」执行同步插件登记`)
    }
    promotionTemplateId.value = meta.templateId
  }

  const loadEditor = async (templateId: number) => {
    loading.value = true
    editorMode.value = 'create'
    activityId.value = undefined
    published.value = false
    try {
      await getMarketPortalEditorInit(templateId).then((data) => {
        initData.value = data
        playStyle.value = data.style || 'wheel'
        styleName.value = data.styleName || '营销活动'
        actName.value = data.suggestedTitle || data.templateName || ''
        shareTitle.value = `${actName.value}，快来参与！`
        shareDesc.value = data.templateName || actName.value
        actRules.value = data.defaultRulesText || ''
        dailyLimit.value = data.defaultDailyLimit ?? 3
        themeColors.value = data.themeColors?.length ? data.themeColors : ['#ff6c6c', '#2b6de8']
        themeColor.value = themeColors.value[0]
        startTime.value = defaultStartTime()
        endTime.value = defaultEndTime(data.defaultDays ?? 7)
        prizes.value = mapPrizeRows(data.defaultPrizes)
        selectedBgId.value = templateId
        promotionTemplateId.value = data.promotionTemplateId
        pageTabs.value = [...PAGE_TABS]
      })
      if (!promotionTemplateId.value) {
        await resolvePromotionTemplate(playStyle.value)
      }
    } catch (e: any) {
      toast(e?.message || '模板玩法暂不可创建')
      throw e
    } finally {
      loading.value = false
    }
  }

  const loadActivity = async (id: number, mode: Exclude<MarketEditorMode, 'create'> = 'edit') => {
    loading.value = true
    editorMode.value = mode
    try {
      const activity = await ActivityApi.getActivity(id)
      activityId.value = activity.id
      activityStatus.value = activity.status
      playStyle.value = activity.style || 'wheel'
      styleName.value = playStyleLabel(playStyle.value)
      promotionTemplateId.value = activity.templateId
      actName.value = activity.name || ''
      startTime.value = normalizeDateTime(activity.startTime) || defaultStartTime()
      endTime.value = normalizeDateTime(activity.endTime) || defaultEndTime(7)
      actRules.value = String(activity.config?.rulesText || '')
      themeColor.value = String(activity.config?.themeColor || '#ff6c6c')
      themeColors.value = ['#ff6c6c', '#ffa940', '#67c23a', '#2b6de8', '#8b6df0', '#f56c6c', '#13c2c2', '#303133']
      shareTitle.value = activity.shareTitle || ''
      shareDesc.value = activity.shareDesc || ''
      needFollow.value = !!activity.followRequired
      antiCheat.value = activity.blacklistEnable !== false
      phoneAuth.value = activity.config?.phoneAuthRequired !== false
      published.value = activity.status === 1
      selectedBgId.value = activity.config?.marketTemplateId
      pageTabs.value = normalizePageTabs(activity.config?.pageTabs)

      const joinRule = activity.rules?.find((r) => r.ruleType === 1)
      dailyLimit.value = joinRule?.dayLimit ?? 3
      shareExtra.value =
        activity.rules?.some((r) => r.ruleType === 2 && (r.shareAddLimit ?? 0) > 0) ?? false

      initData.value = {
        marketTemplateId: activity.config?.marketTemplateId,
        coverUrl: activity.coverUrl || activity.shareCoverUrl,
        playPagePath: '/pages/modules/promotion/pages/play/index',
        style: playStyle.value,
        styleName: styleName.value
      } as MarketPortalEditorInitVO

      if (LOTTERY_STYLES.has(playStyle.value)) {
        const prizeList = await PrizeApi.getPrizeListByActivity(id)
        prizes.value = mapActivityPrizeRows(prizeList)
      } else {
        prizes.value = []
      }

      if (!promotionTemplateId.value) {
        await resolvePromotionTemplate(playStyle.value)
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '加载活动失败'
      toast(msg.includes('replace') ? '活动数据格式异常，请刷新列表后重试' : msg || '加载活动失败')
      throw e
    } finally {
      loading.value = false
    }
  }

  const buildRules = (): ActivityApi.ActivityRuleVO[] => {
    const rules: ActivityApi.ActivityRuleVO[] = []
    if (LOTTERY_STYLES.has(playStyle.value)) {
      rules.push({
        ruleType: 1,
        dayLimit: dailyLimit.value > 0 ? dailyLimit.value : 0,
        totalLimit: 0,
        shareAddLimit: 0,
        shareMaxLimit: 0,
        status: 0
      })
      if (shareExtra.value) {
        rules.push({
          ruleType: 2,
          dayLimit: 0,
          totalLimit: 0,
          shareAddLimit: 1,
          shareMaxLimit: 0,
          status: 0
        })
      }
    } else {
      rules.push({
        ruleType: 1,
        dayLimit: dailyLimit.value > 0 ? dailyLimit.value : 0,
        totalLimit: 0,
        shareAddLimit: shareExtra.value ? 1 : 0,
        shareMaxLimit: 0,
        status: 0
      })
    }
    return rules
  }

  const buildActivityPayload = (): ActivityApi.ActivitySaveReqVO => {
    const coverFromTemplate = selectedBgId.value ? getCoverUrl(selectedBgId.value) : undefined
    const cover = coverFromTemplate || initData.value?.coverUrl || ''
    return {
      id: activityId.value,
      templateId: promotionTemplateId.value,
      style: playStyle.value,
      name: actName.value,
      coverUrl: cover,
      shareCoverUrl: cover,
      startTime: toBackendDateTime(startTime.value),
      endTime: toBackendDateTime(endTime.value),
      shareTitle: shareTitle.value || actName.value,
      shareDesc: shareDesc.value,
      followRequired: needFollow.value,
      blacklistEnable: antiCheat.value,
      config: {
        themeColor: themeColor.value,
        rulesText: actRules.value,
        phoneAuthRequired: phoneAuth.value,
        antiCheatEnabled: antiCheat.value,
        marketTemplateId: initData.value?.marketTemplateId,
        marketRstyle: initData.value?.rstyle,
        marketPlayStyle: playStyle.value,
        pageTabs: pageTabs.value,
        prizes: buildConfigPrizeSnapshot(prizes.value)
      },
      rules: buildRules()
    }
  }

  const validateBeforeSave = (publish: boolean): string | null => {
    if (!actName.value.trim()) return '请填写活动名称'
    if (!startTime.value || !endTime.value) return '请填写活动时间'
    if (new Date(endTime.value) <= new Date(startTime.value)) return '结束时间必须晚于开始时间'
    if (!promotionTemplateId.value) return '玩法模板未就绪，请刷新后重试'
    if (publish && LOTTERY_STYLES.has(playStyle.value) && !prizes.value.length) {
      return '发布前请至少配置一个奖品'
    }
    if (publish && LOTTERY_STYLES.has(playStyle.value)) {
      const badCoupon = prizes.value.find(
        (p) => p.type === '优惠券' || prizeTypeFromLabel(String(p.type), p.prizeType) === 1
      )
      if (badCoupon) {
        return `优惠券奖品「${badCoupon.name}」须先在「编辑活动」中关联「指定发放」券模板后再发布`
      }
    }
    return null
  }

  const syncPrizes = async (id: number) => {
    if (!LOTTERY_STYLES.has(playStyle.value)) return
    const existing = await PrizeApi.getPrizeListByActivity(id)
    for (const old of existing || []) {
      if (old.id) await PrizeApi.deletePrize(old.id)
    }
    const probSum = prizes.value.reduce((s, p) => s + Number(p.probability || 0), 0) || 100
    let sort = 0
    for (const row of prizes.value) {
      const prizeType = prizeTypeFromLabel(String(row.type), row.prizeType)
      const winRate = Math.max(1, Math.round((Number(row.probability || 0) / probSum) * 10000))
      const totalStock = Number(row.count) || 0
      await PrizeApi.createPrize({
        activityId: id,
        name: row.name,
        prizeType,
        totalStock,
        restStock: totalStock,
        winRate,
        sorted: sort++,
        status: 0
      })
    }
  }

  const saveDraft = async (silent = false): Promise<number | undefined> => {
    const err = validateBeforeSave(false)
    if (err) {
      toast(err)
      return undefined
    }
    saving.value = true
    try {
      const payload = buildActivityPayload()
      let id = activityId.value
      if (id) {
        await ActivityApi.updateActivity(payload)
      } else {
        id = await ActivityApi.createActivity(payload)
        activityId.value = id
      }
      await syncPrizes(id!)
      if (!silent) toast('已保存草稿')
      return id
    } catch (e: any) {
      if (!silent) toast(e?.message || '保存失败，请检查权限或活动配置')
      return undefined
    } finally {
      saving.value = false
    }
  }

  const publish = async (): Promise<boolean> => {
    const err = validateBeforeSave(true)
    if (err) {
      toast(err)
      return false
    }
    saving.value = true
    try {
      const id = (await saveDraft(true)) ?? activityId.value
      if (!id) return false
      await ActivityApi.publishActivity(id)
      published.value = true
      toast('发布成功')
      return true
    } catch (e: any) {
      toast(e?.message || '发布失败')
      return false
    } finally {
      saving.value = false
    }
  }

  const preview = async () => {
    const id = activityId.value ?? (await saveDraft())
    if (!id) return
    try {
      const token = await ActivityApi.generatePreviewToken(id)
      const path = initData.value?.playPagePath || '/pages/modules/promotion/pages/play/index'
      const url = `${mobileH5Base()}${path}?activityId=${id}&style=${playStyle.value}&previewToken=${token}`
      await navigator.clipboard.writeText(url)
      toast('预览链接已复制')
    } catch {
      toast('预览链接生成失败，请先保存活动')
    }
  }

  const copyLink = async () => {
    if (!activityLink.value) {
      toast('请先保存活动')
      return
    }
    try {
      await navigator.clipboard.writeText(activityLink.value)
      toast('链接已复制')
    } catch {
      toast(activityLink.value)
    }
  }

  return {
    loading,
    saving,
    initData,
    activityId,
    playStyle,
    styleName,
    published,
    editorTitle,
    activityLink,
    editorMode,
    readonly,
    activityStatus,
    isLotteryStyle: computed(() => isLotteryPlayStyle(playStyle.value)),
    actName,
    startTime,
    endTime,
    actRules,
    dailyLimit,
    shareExtra,
    shareTitle,
    shareDesc,
    needFollow,
    phoneAuth,
    antiCheat,
    themeColor,
    themeColors,
    selectedBgId,
    prizes,
    pageTabs,
    loadEditor,
    loadActivity,
    saveDraft,
    publish,
    preview,
    copyLink
  }
}
